// Settings Types - TypeScript interfaces matching C# DTOs
// Based on AdventureCore Settings API

// ====================
// ENUMS
// ====================

export enum NotificationFrequency {
  Immediately = 0,
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
  Never = 4,
}

export enum Theme {
  Light = 'Light',
  Dark = 'Dark',
  System = 'System',
}

export enum Language {
  English = 'en',
  Spanish = 'es',
  French = 'fr',
  German = 'de',
}

export enum MfaStatus {
  Disabled = 'Disabled',
  Enabled = 'Enabled',
  Required = 'Required', // Admin enforced
}

export enum DistanceUnit {
  Miles = 'Miles',
  Kilometers = 'Kilometers',
}

export enum MeasurementUnit {
  Metric = 0,
  Imperial = 1,
}

// ====================
// USER PROFILE
// ====================

export enum ProfileVisibility {
  Public = 0,
  FriendsOnly = 1,
  Private = 2,
}

export interface SocialMediaLinkDto {
  platform: string | null
  url: string | null
  isPublic: boolean
}

// For backwards compatibility
export type SocialMediaLink = SocialMediaLinkDto

export interface BillingAddressDto {
  addressLine1: string
  addressLine2: string
  city: string
  stateOrProvince: string
  postalCode: string
  country: string
}

export interface UserProfileDto {
  id: string
  userId: string | null
  email: string | null
  displayName: string | null
  avatar: string | null // API uses "avatar", not "profileImageUrl"
  coverPhoto: string | null // API uses "coverPhoto", not "coverPhotoUrl"
  bio: string | null
  hometown: string | null

  // Profile visibility
  profileVisibility: ProfileVisibility
  showAge: boolean
  socialLinksVisible: boolean

  // Age in months (not birthDateDays)
  ageInMonths: number | null

  // Gender identity as string (not tagId)
  genderIdentity: string | null

  // Computed/read-only
  ageRange: string | null

  // Social media links (dictionary, not array)
  socialMediaLinks: Record<string, SocialMediaLinkDto> | null

  // Interest tags as strings (not IDs)
  interestTags: string[] | null

  // Looking for as strings (not IDs)
  lookingFor: string[] | null

  // Rank info
  rank: number
  rankName: string | null
  rankBadge: string | null
  rankInfo: string | null
  nextRankName: string | null
  pointsToNextRank: number

  // Dates
  createdDate: string | null
  updatedDate: string | null
  lastDisplayNameChange: string | null
  displayNameChangesThisYear: number
}

// Location settings nested object
export interface LocationSettingsDto {
  userId?: string | null
  homeLatitude?: number | null
  homeLongitude?: number | null
  homeLocationLastUpdated?: string | null
  locationSharingEnabled?: boolean
  locationSharingMinHideDistanceMiles?: number | null
  locShare_Group?: boolean
  locShare_Friends?: boolean
  showLocationToAdventureParticipants?: boolean
  showHometown?: boolean
}

// Extended account DTO with preferences and settings
export interface UserAccountDto extends UserProfileDto {
  // Preferences
  theme?: string | null
  language?: string | null
  measurementUnit?: number | null
  showLocationOnProfile?: boolean
  showOnlineStatus?: boolean

  // Privacy settings (legacy - some may still be used)
  showHometown?: boolean
  showLocationToFriends?: boolean
  showLocationToAdventureParticipants?: boolean

  // Home location (legacy - use locationSettings instead)
  homeLatitude?: number | null
  homeLongitude?: number | null
  locationSharingMinHideDistanceMiles?: number | null

  // New nested location settings
  locationSettings?: LocationSettingsDto | null

  // Billing address
  billingAddress?: BillingAddressDto | null

  // Username field
  userName?: string | null
}

export interface AddressDto {
  street?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
}

export interface UpdateUserProfileDto {
  displayName: string | null
  avatar: string | null
  coverPhoto: string | null
  bio: string | null
  hometown: string | null
  profileVisibility: ProfileVisibility
  showAge: boolean
  socialLinksVisible: boolean
  ageInMonths: number | null
  genderIdentity: string | null
  socialMediaLinks: Record<string, SocialMediaLinkDto> | null
  interestTags: string[] | null
  lookingFor: string[] | null
  displayNameChangesThisYear: number
}

// Legacy alias for backwards compatibility
export type UpdateUserProfileRequest = UpdateUserProfileDto

export interface PasswordResetRequest {
  email: string
}

// ====================
// NOTIFICATION SETTINGS
// ====================

export interface NotificationPreferencesDto {
  // Email notifications
  emailNotifications: EmailNotificationPreferences

  // SMS notifications
  smsNotifications: SmsNotificationPreferences
}

export interface EmailNotificationPreferences {
  adventureInvites: NotificationFrequency
  adventureUpdates: NotificationFrequency
  messages: NotificationFrequency
  friendRequests: NotificationFrequency
  achievements: NotificationFrequency
  systemAnnouncements: NotificationFrequency
}

export interface SmsNotificationPreferences {
  adventureInvites: boolean
  adventureReminders: boolean
  emergencyAlerts: boolean
}

export interface UpdateNotificationPreferencesRequest {
  emailNotifications: EmailNotificationPreferences
  smsNotifications: SmsNotificationPreferences
}

// New notification settings from API update
export interface UserNotificationSettingsDto {
  email: EmailNotificationSettingsDto
  sms: SmsNotificationSettingsDto
}

export interface EmailNotificationSettingsDto {
  summaryOverview: boolean
  newPublicActivities: NotificationFrequency
  friendRequests: NotificationFrequency
  publishedItemActivities: NotificationFrequency
  messages: NotificationFrequency
}

export interface SmsNotificationSettingsDto {
  enabled: boolean
  frequency: NotificationFrequency
  friendRequests: boolean
  messages: boolean
}

// ====================
// USER PREFERENCES
// ====================

export interface UserPreferencesDto {
  theme: Theme
  language: Language
  measurementUnit?: MeasurementUnit
  showLocationOnProfile: boolean
  showOnlineStatus: boolean
}

export interface UpdateUserPreferencesRequest {
  theme: Theme
  language: Language
  measurementUnit?: MeasurementUnit
  showLocationOnProfile: boolean
  showOnlineStatus: boolean
}

// ====================
// MULTI-FACTOR AUTHENTICATION (MFA)
// ====================

export interface MfaStatusDto {
  isEnabled: boolean
  isRequired: boolean // Admin enforced
  setupDate?: string
  lastUsedDate?: string
  backupCodesRemaining: number
}

export interface MfaSetupDto {
  secret?: string | null
  qrCodeUrl?: string | null
  manualEntryCode?: string | null
  backupCodes?: string[] | null
}

export interface MfaEnableRequest {
  verificationCode: string
}

export interface MfaVerifyRequest {
  code: string
}

export interface MfaBackupCodesDto {
  success: boolean
  message?: string | null
  backupCodes?: string[] | null
}

// ====================
// USER SESSIONS
// ====================

export interface UserSessionDto {
  id: string
  ipAddress: string
  userAgent: string
  city?: string
  state?: string
  country?: string
  createdAt: string
  lastAccessedAt: string
  expiresAt: string
  isRevoked: boolean
  revokedAt?: string | null
  isCurrentSession: boolean
}

export interface DeleteSessionsDto {
  sessionId?: string
  deleteAllExceptCurrent?: boolean
}

export interface DeleteSessionsResponseDto {
  success: boolean
  message?: string
  sessionsDeleted: number
}

// ====================
// HOME LOCATION
// ====================

export interface HomeLocationDto {
  latitude: number
  longitude: number
  address?: string
  city?: string
  state?: string
  country?: string
  lastUpdatedDate: string
  canUpdateUntil: string // Monthly update restriction
}

export interface UpdateHomeLocationRequest {
  latitude: number
  longitude: number
  address?: string
  city?: string
  state?: string
  country?: string
}

// ====================
// LOCATION SHARING
// ====================

export interface LocationSharingSettingsDto {
  shareLocationWithFriends: boolean
  shareLocationWithAdventureParticipants: boolean
  shareLocationPublicly: boolean
  shareHomeLocation: boolean
  shareCurrentLocation: boolean

  // Granular controls
  showCityOnly: boolean // Hide exact coordinates, show only city
  showCountryOnly: boolean // Hide city, show only country

  // Profile privacy (from API update)
  showHometown?: boolean
}

export interface UpdateLocationSharingSettingsRequest {
  shareLocationWithFriends: boolean
  shareLocationWithAdventureParticipants: boolean
  shareLocationPublicly: boolean
  shareHomeLocation: boolean
  shareCurrentLocation: boolean
  showCityOnly: boolean
  showCountryOnly: boolean
  showHometown?: boolean
}

// ====================
// COMBINED SETTINGS
// ====================

export interface UserSettingsDto {
  profile: UserProfileDto
  notifications: NotificationPreferencesDto
  preferences: UserPreferencesDto
  mfaStatus: MfaStatusDto
  locationSharing: LocationSharingSettingsDto
}

// ====================
// PRIVACY SETTINGS (Chat Age Restrictions)
// ====================

export interface UserPrivacySettingsDto {
  chatAgeRestrictionEnabled: boolean
  chatMinAgeRestriction: number | null
  chatMaxAgeRestriction: number | null
  isParentManaged: boolean
}

export interface UpdateChatAgeRestrictionRequest {
  enabled: boolean
  minAge: number
  maxAge: number
}

// ====================
// PARENTAL CONTROLS
// ====================

export enum ParentalLinkStatus {
  Pending = 0,
  Active = 1,
  Revoked = 2,
  Expired = 3,
}

export interface ParentalLinkDto {
  id: string
  parentUserId: string
  childUserId: string
  status: ParentalLinkStatus
  verificationCode?: string | null
  verificationExpires?: string | null
  createdAt: string
  confirmedAt?: string | null
  revokedAt?: string | null
  revokedBy?: string | null
}

export interface CreateParentalLinkRequest {
  childIdentifier: string
}

export interface CreateParentalLinkResponse {
  id: string
  parentUserId: string
  parentDisplayName: string | null
  childUserId: string
  childDisplayName: string | null
  status: ParentalLinkStatus
  createdAt: string
  confirmedAt: string | null
  verificationCode: string | null
}

export interface ConfirmParentalLinkRequest {
  verificationCode: string
}

export interface SetChildPrivacySettingsRequest {
  childUserId: string
  chatAgeRestrictionEnabled: boolean
  chatMinAgeRestriction: number | null
  chatMaxAgeRestriction: number | null
}

export interface ManagedChildAccountDto {
  linkId: string
  childUserId: string
  childDisplayName: string | null
  childAvatarUrl: string | null
  linkStatus: ParentalLinkStatus
  linkedAt: string
  privacySettings: UserPrivacySettingsDto
}

export interface ParentAccountDto {
  linkId: string
  parentUserId: string
  parentDisplayName: string | null
  parentAvatarUrl: string | null
  linkStatus: ParentalLinkStatus
  linkedAt: string
}

export interface PendingParentalLinkDto {
  id: string
  parentUserId: string
  parentDisplayName: string | null
  childUserId: string
  childDisplayName: string | null
  status: ParentalLinkStatus
  createdAt: string
  confirmedAt: string | null
  verificationCode: string | null // Not exposed to child
}

export interface PendingOutboundLinkDto {
  id: string
  parentUserId: string
  parentDisplayName: string | null
  childUserId: string
  childDisplayName: string | null
  status: ParentalLinkStatus
  createdAt: string
  confirmedAt: string | null
  verificationCode: string | null
}

// ====================
// API RESPONSES
// ====================

export interface SettingsApiResponse<T> {
  success: boolean
  data?: T
  message?: string
  errors?: string[]
}
